import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import type { AuditCategory } from '@/utils/api-pb';

interface CategoryScoreBarProps {
  category: AuditCategory;
  /** null — категория не проверялась (например, оффер без прикреплённого проекта). */
  score: number | null;
  issues: number;
}

/**
 * Colour by score, not by category.
 *
 * Three bands rather than a gradient: the bar answers "is this the thing to fix
 * first", and a continuous scale makes 61 and 68 look like different answers when they
 * are not.
 */
function toneOf(score: number): string {
  if (score >= 80) return 'bg-[#2e7d32]';
  if (score >= 50) return 'bg-[#e8a33d]';
  return 'bg-[color:var(--error-strong,#b3261e)]';
}

export function CategoryScoreBar({ category, score, issues }: CategoryScoreBarProps) {
  const { t } = useTranslation('siteAudit');

  return (
    <div className="flex flex-col gap-[6px]">
      <div className="flex items-baseline justify-between gap-[8px]">
        <span className="font-manrope text-[14px] font-semibold text-[color:var(--black-text,#0d0d0d)]">
          {t(`category.${category}`)}
        </span>
        <span className="font-segoe text-[13px] text-[color:var(--grey-text,#565858)]">
          {/* Непроверенная категория — не «ноль» и не «сто»: так и написано. */}
          {score === null ? t('report.notChecked') : t('report.categoryScore', { score, issues })}
        </span>
      </div>

      <div
        className="h-[6px] w-full overflow-hidden rounded-full bg-[var(--grey-accent,#eeeeee)]"
        role="progressbar"
        aria-valuenow={score ?? undefined}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={t(`category.${category}`)}
      >
        {score !== null && (
          <div className={cn('h-full rounded-full transition-[width]', toneOf(score))} style={{ width: `${score}%` }} />
        )}
      </div>
    </div>
  );
}
